import { useMemo } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import {
  useVariableDescriptionContext,
  ActivityLoggedContext,
  DisabledTypes,
  ActivityPastDueContext,
  WebhookTriggerContext,
} from '../../VariableWizardContext';
import { SectionOptions, SectionSubOption } from './types';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import BooleanIcon from './images/boolean.svg?react';
import DateIcon from './images/date.svg?react';
import DateTimeIcon from './images/datetime.svg?react';
import NumberIcon from './images/number.svg?react';
import PhoneNumberIcon from './images/phonenumber.svg?react';
import StringIcon from './images/string.svg?react';
import UuidIcon from './images/uuid.svg?react';
import TeamMemberIcon from './images/teammember.svg?react';
import EntityIcon from './images/entity.svg?react';
import { useAutomationsSelector } from '__pages/AutomationEngine/store/react';
import { getAutomation } from '__pages/AutomationEngine/store/selectors';
import { isTypeRecordBased } from 'pages/Automations/helpers';
import { Automation } from 'store/automationsPage/automations.redux';
import { getSubtypeLabel } from 'pages/AutomationEngine/helpers';
import type {
  AutomationVariableDataType,
  GenericOption,
} from 'pages/AutomationEngine/types';

const getSections = ({
  activityLoggedContext,
  activityPastDueContext,
  webhookTriggerContext,
  disabledTypes,
  forInitialize,
  automation,
  t,
}: {
  activityLoggedContext: ActivityLoggedContext;
  activityPastDueContext: ActivityPastDueContext;
  webhookTriggerContext: WebhookTriggerContext;
  disabledTypes: DisabledTypes;
  forInitialize: boolean;
  automation: Automation;
  t: TFunction;
}): SectionOptions[] => {
  const haveLoggedActivities = activityLoggedContext.activities.length;
  const havePastDueActivities = activityPastDueContext.activities.length;
  const haveWebhookTriggers = !!webhookTriggerContext.webhooks.length;

  const sections: SectionOptions[] = [];

  const showActivitiesOnButton =
    (haveLoggedActivities && !disabledTypes.logged_activity_trigger) ||
    (havePastDueActivities && !disabledTypes.activity_past_due_trigger);

  const showWebhookOnButton =
    haveWebhookTriggers && !disabledTypes.webhook_trigger;

  if (showActivitiesOnButton) {
    const options: SectionSubOption[] = [];
    if (haveLoggedActivities && !disabledTypes.logged_activity_trigger) {
      options.push({
        value: 'logged_activity_trigger',
        label: t('Logged Activity Value'),
        disabled:
          !haveLoggedActivities || disabledTypes.logged_activity_trigger,
      });
    }
    if (havePastDueActivities && !disabledTypes.activity_past_due_trigger) {
      options.push({
        value: 'activity_past_due_trigger',
        label: t('Activity Past Due'),
        disabled:
          !havePastDueActivities || disabledTypes.activity_past_due_trigger,
      });
    }

    sections.push({
      label: t('Activities'),
      tooltip: t('Only applicable with Activity triggers'),
      options,
    });
  }

  if (isTypeRecordBased(automation)) {
    sections.push({
      label: t('Fields'),
      tooltip: undefined,
      options: [
        {
          value: 'field_value',
          label: t('Field Value'),
          disabled: false,
        },
      ],
    });
  }

  if (!forInitialize) {
    sections.push({
      label: t('Agentic Workflow Variable'),
      options: [
        {
          value: 'variable',
          label: t('Agentic Workflow Variable'),
          disabled: false,
        },
      ],
    });
  }

  if (forInitialize && (!disabledTypes.trigger_time || showWebhookOnButton)) {
    const triggerOptions: SectionSubOption[] = [];

    if (!disabledTypes.trigger_time) {
      triggerOptions.push({
        value: 'trigger_time',
        label: t('Trigger Time'),
        disabled: false,
      });
    }
    if (showWebhookOnButton) {
      triggerOptions.push({
        value: 'webhook_trigger',
        label: t('Webhook Data'),
        disabled: false,
      });
    }

    sections.push({
      label: t('Trigger'),
      options: triggerOptions,
    });
  }

  return sections;
};

export const useGetButtonGridOption = () => {
  const { t } = useTranslation();

  return useMemo<
    {
      label: string;
      options: (GenericOption<AutomationVariableDataType> & {
        image: JSX.Element;
      })[];
    }[]
  >(
    () => [
      {
        label: t('Standard Value Variables'),
        options: [
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.boolean,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.boolean,
              },
              t
            ),
            image: <BooleanIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.date,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.date,
              },
              t
            ),
            image: <DateIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.datetime,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.datetime,
              },
              t
            ),
            image: <DateTimeIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.number,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.number,
              },
              t
            ),
            image: <NumberIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.phone_number,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.phone_number,
              },
              t
            ),
            image: <PhoneNumberIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.string,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.string,
              },
              t
            ),
            image: <StringIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.uuid,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.uuid,
              },
              t
            ),
            image: <UuidIcon />,
          },
        ],
      },
      {
        label: t('Kizen Entities as Variables'),
        options: [
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.employee,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.employee,
              },
              t
            ),
            image: <TeamMemberIcon />,
          },
          {
            value: AUTOMATION_VARIABLE_DATA_TYPES.entity,
            label: getSubtypeLabel(
              {
                type: 'initialize_variable',
                subtype: AUTOMATION_VARIABLE_DATA_TYPES.entity,
              },
              t
            ),
            image: <EntityIcon />,
          },
        ],
      },
    ],
    [t]
  );
};

export const useGetVariableDefinitionOptions = (forInitialize: boolean) => {
  const { t } = useTranslation();
  const {
    activityLoggedContext,
    activityPastDueContext,
    webhookTriggerContext,
    disabledTypes,
  } = useVariableDescriptionContext();

  const automation = useAutomationsSelector(getAutomation);

  return useMemo(
    () =>
      getSections({
        t,
        activityLoggedContext,
        activityPastDueContext,
        webhookTriggerContext,
        disabledTypes,
        forInitialize,
        automation,
      }),
    [
      activityLoggedContext,
      activityPastDueContext,
      webhookTriggerContext,
      disabledTypes,
      forInitialize,
      automation,
      t,
    ]
  );
};
